import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Container, Row } from "react-bootstrap";
import { Plus } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import i18n from "../../../i18n";
import { createProject, fetchProjects } from "../../../redux/slices/projectsSlice";
import CreateEditProjectModal from "../CreateEditProjectModal/CreateEditProjectModal";
import ProjectsList from "../ProjectsList/ProjectsList";
import "../projectsFeature.css";

const createInitialProject = () => ({
  name: "",
  description: "",
  start_date: "",
  end_date: "",
  estimated_etis: "",
  budget: "",
  priority: "Medium",
  project_type: "Internal",
  status: "Planning",
  color: "#0A2368"
});

const ManageProjects = () => {
  const dispatch = useDispatch();
  const { items: projects = [], isLoading, error } = useSelector((state) => state.projects);

  const [projectDraft, setProjectDraft] = useState(createInitialProject);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    dispatch(fetchProjects());
  }, [dispatch]);

  const metrics = useMemo(() => {
    const totalProjects = projects.length;
    const activeProjects = projects.filter(
      (project) => String(project?.status || "").toLowerCase() === "active"
    ).length;
    const totalBudget = projects.reduce((sum, project) => sum + (Number(project?.budget) || 0), 0);

    return {
      totalProjects,
      activeProjects,
      totalBudget,
    };
  }, [projects]);

  const budgetLabel = useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }).format(metrics.totalBudget || 0),
    [metrics.totalBudget]
  );

  const handleOpenModal = () => {
    setProjectDraft(createInitialProject());
    setShowModal(true);
  };

  const handleCreate = async (payload) => {
    const result = await dispatch(createProject(payload));

    if (!result.error) {
      setShowModal(false);
      setProjectDraft(createInitialProject());
      dispatch(fetchProjects());
    }
  };

  return (
    <Container className="py-4 projects-shell fade-in-up">
      <div className="projects-toolbar mb-4">
        <div className="mt-5">
          <h2 className="projects-title mb-1">{i18n.t("pages.ManageProjects.my_projects")}</h2>
          <p className="projects-subtitle mb-0">{i18n.t("pages.ManageProjects.my_projects_description")}</p>
        </div>
        <Button className="projects-cta" onClick={handleOpenModal}>
          <Plus size={16} />
          <span>{i18n.t("components.CreateEditProjectModal.create_project")}</span>
        </Button>
      </div>

      <Row className="g-3 mb-4">
        <Col xs={12} md={4}>
          <article className="projects-metric-card">
            <p className="projects-metric-label mb-1">{i18n.t("pages.ManageProjects.total_projects")}</p>
            <h4 className="mb-0">{metrics.totalProjects}</h4>
          </article>
        </Col>
        <Col xs={12} md={4}>
          <article className="projects-metric-card">
            <p className="projects-metric-label mb-1">{i18n.t("pages.ManageProjects.active_projects")}</p>
            <h4 className="mb-0">{metrics.activeProjects}</h4>
          </article>
        </Col>
        <Col xs={12} md={4}>
          <article className="projects-metric-card">
            <p className="projects-metric-label mb-1">{i18n.t("pages.ManageProjects.portfolio_budget")}</p>
            <h4 className="mb-0">{budgetLabel}</h4>
          </article>
        </Col>
      </Row>

      {error && <Alert variant="danger">{String(error)}</Alert>}

      <CreateEditProjectModal
        showModal={showModal}
        setShowModal={setShowModal}
        onSubmit={handleCreate}
        projectData={projectDraft}
        setProjectData={setProjectDraft}
        isCreate
        isSubmitting={isLoading}
      />

      <ProjectsList projects={projects} isLoading={isLoading} error={error} />
    </Container>
  );
};

export default ManageProjects;
